import React, {useState} from 'react'
import {Box, Button, Card, Checkbox, Chip, Divider, FormControlLabel, Typography} from '@mui/material'
import type {CrosstabsObjectivesData} from './types'

interface ObjectivesWidgetProps {
  data: CrosstabsObjectivesData
  onSubmit: (formattedMessage: string) => void
}

const ObjectivesWidget: React.FC<ObjectivesWidgetProps> = ({data, onSubmit}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [noneSelected, setNoneSelected] = useState(false)

  const handleToggle = (id: string) => {
    setNoneSelected(false)
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleNone = () => {
    setSelected(new Set())
    setNoneSelected(prev => !prev)
  }

  const handleSubmit = () => {
    if (noneSelected) {
      onSubmit(data.noneOptionLabel || "None selected — I'll describe my own analysis")
      return
    }
    if (selected.size === 0) return
    const ids = Array.from(selected).sort()
    onSubmit(`Selected objectives: ${ids.join(', ')}`)
  }

  const canSubmit = selected.size > 0 || noneSelected

  const bannerSummary = data.coreBanners.map(b => b.label).join(', ')

  return (
    <Box
      sx={{
        mx: 0,
        mb: 1,
        p: 2,
        border: 1,
        borderColor: 'primary.light',
        borderRadius: 2,
        bgcolor: 'background.paper',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{mb: 0.5}}>
        Select Research Objectives
      </Typography>
      {bannerSummary && (
        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1.5}}>
          Core banners: {bannerSummary}
        </Typography>
      )}

      <Box sx={{maxHeight: 'calc(50vh - 100px)', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5}}>
        {data.objectives.map(obj => {
          const isChecked = selected.has(obj.id)
          return (
            <Card
              key={obj.id}
              variant="outlined"
              onClick={() => handleToggle(obj.id)}
              sx={{
                px: 1.5,
                py: 1,
                cursor: 'pointer',
                borderColor: isChecked ? 'primary.main' : 'divider',
                bgcolor: isChecked ? 'primary.50' : 'background.paper',
                '&:hover': {borderColor: 'primary.light'}
              }}
            >
              <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                <Checkbox
                  checked={isChecked}
                  size="small"
                  sx={{p: 0, mt: 0.25}}
                  onClick={e => e.stopPropagation()}
                  onChange={() => handleToggle(obj.id)}
                />
                <Box sx={{flex: 1, minWidth: 0}}>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.25}}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {obj.title}
                    </Typography>
                    <Chip
                      label={`${obj.tableCount} table${obj.tableCount !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                      sx={{height: 24, fontSize: '0.75rem'}}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{display: 'block', lineHeight: 1.4, mb: 0.5}}>
                    {obj.story}
                  </Typography>
                  <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                    {obj.stubs.slice(0, 3).map(s => (
                      <Chip
                        key={s.variable}
                        label={`${s.label} (${s.tableType})`}
                        size="small"
                        sx={{height: 22, fontSize: '0.75rem', bgcolor: 'grey.100'}}
                      />
                    ))}
                    {obj.stubs.length > 3 && (
                      <Chip
                        label={`+${obj.stubs.length - 3} more`}
                        size="small"
                        sx={{height: 22, fontSize: '0.75rem', bgcolor: 'grey.100'}}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Card>
          )
        })}
      </Box>

      <Divider sx={{my: 1}} />

      <FormControlLabel
        control={
          <Checkbox
            checked={noneSelected}
            onChange={handleNone}
            size="small"
          />
        }
        label={
          <Typography variant="caption" color="text.secondary">
            {data.noneOptionLabel || "I'd rather describe my own analysis"}
          </Typography>
        }
        sx={{ml: 0, mb: 1}}
      />

      <Button
        variant="contained"
        size="small"
        onClick={handleSubmit}
        disabled={!canSubmit}
        sx={{textTransform: 'none'}}
      >
        Submit
      </Button>
    </Box>
  )
}

export default ObjectivesWidget
